import clsx from 'clsx';
import map from 'lodash/map';
import { ColumnsType } from 'rc-table/lib/interface';
import React from 'react';
import { DataTableType, Table, TableCell, TableHeadCell } from '@/components/common/Table';
import { CryptoFrontendStatus, useMerchantPayments } from '@/api/queryHooks/useTransactionsController';
import { useTableHelpers } from '@/components/common/Table/useTableHelpers';
import { formatDateAndTime, formatNumberWithCommas } from '@/utils/functions';
import { FiatCurrency, MerchantPayment } from '@/api/common.types';
import { AmountAndCurrency } from '@/views/Transactions/components/TransactionsTable/types';
import { RowStatus } from '@/views/Transactions/components/TransactionsTable/RowStatus';
import { RowTxnButton } from '@/views/Transactions/components/TransactionsTable/RowTxnButton';
import { CopyButton } from '../../../../../components/common/buttons';
import styles from './TransactionsTableMerchant.module.css';

function mapMerchantPaymentsData(data?: MerchantPayment[]): DataTableType[] {
  return map(data, (item) => {
    const { date, time } = formatDateAndTime(item.created);

    return {
      key: item.transactionId ?? Math.random(),
      orderId: item.orderId?.split('-')[0],
      date: `${time}, ${date}`,
      payerEmail: item.payerEmail,
      paymentStatus: item.paymentStatus,
      settlementStatus: item.settlementStatus,
      totalAmount: { amount: formatNumberWithCommas(item.fiatAmount ?? 0) || 0, currency: item.fiatCurrency },
      expandedData: item?.blockChainExplorer
        ? {
            href: `${item?.blockChainExplorer}${item.blockchainTxHash || ''}`,
            txHash: item.blockchainTxHash || '',
          }
        : { href: undefined, txHash: item.blockchainTxHash || '' },
    };
  });
}

export const TransactionsTableMerchant = () => {
  const { sortBy, isDesc, toggleSort, page, setPage, tableData, setTableData } = useTableHelpers();
  const { data } = useMerchantPayments([
    { sortField: sortBy, isDesc, page },
    {
      keepPreviousData: true,
      refetchOnMount: true,
      onSuccess(data) {
        setTableData(mapMerchantPaymentsData(data?.content));
      },
    },
  ]);

  const columns: ColumnsType<DataTableType> = [
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('created')} active={sortBy === 'created'} isDesc={isDesc}>
          Payment Date
        </TableHeadCell>
      ),
      align: 'left',
      dataIndex: 'date',
      render: (value: string) => (
        <TableCell isBold>
          {/* <TableCell expandable isBold> */}
          {value}
        </TableCell>
      ),
      key: 'date',
    },
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('orderId')} active={sortBy === 'orderId'} isDesc={isDesc}>
          Order ID
        </TableHeadCell>
      ),
      align: 'left',
      dataIndex: 'orderId',
      render: (value: string) => <TableCell withEllipsis>{value}</TableCell>,
      key: 'orderId',
    },

    {
      title: (
        <TableHeadCell onClick={() => toggleSort('email')} active={sortBy === 'email'} isDesc={isDesc}>
          Payer Email
        </TableHeadCell>
      ),
      align: 'left',
      dataIndex: 'payerEmail',
      render: (value: string) => <TableCell withEllipsis>{value}</TableCell>,
      key: 'payerEmail',
    },
    {
      title: <TableHeadCell sort={false}>Payment Status</TableHeadCell>,
      align: 'right',
      dataIndex: 'paymentStatus',
      render: (value: CryptoFrontendStatus) => (
        <TableCell>
          <RowStatus statusFromBE={value} />
        </TableCell>
      ),
      key: 'paymentStatus',
    },
    {
      title: <TableHeadCell sort={false}>Settlement Status</TableHeadCell>,
      align: 'right',
      dataIndex: 'settlementStatus',
      render: (value: CryptoFrontendStatus) => (
        <TableCell>
          <RowStatus statusFromBE={value} />
        </TableCell>
      ),
      key: 'settlementStatus',
    },
    {
      title: (
        <TableHeadCell onClick={() => toggleSort('fiatAmount')} active={sortBy === 'fiatAmount'} isDesc={isDesc}>
          Total Amount
        </TableHeadCell>
      ),
      align: 'right',
      dataIndex: 'totalAmount',
      render: (value: AmountAndCurrency<FiatCurrency>) => (
        <TableCell>
          ${value?.amount} {value?.currency}
        </TableCell>
      ),
      key: 'totalAmount',
    },
  ];

  const expandedRowRender = (record: DataTableType) => {
    return (
      <div className={styles.extra}>
        <span className={styles.extraName}>Transactions Hash:</span>
        {record?.expandedData?.txHash ? (
          <>
            <span className={clsx(styles.extraValue, styles.copy)}>
              <CopyButton variants="hash" iconPosition="right">
                {record?.expandedData.txHash}
              </CopyButton>
            </span>
            {record?.expandedData?.href && <RowTxnButton href={record?.expandedData?.href as string} />}
          </>
        ) : (
          '---'
        )}
      </div>
    );
  };

  return (
    <Table
      data={tableData}
      columns={columns}
      totalElements={data?.totalElements}
      pageSize={data?.size}
      onPaginate={setPage}
      // Turn on/off expandable mode
      expandable={false}
      expandedRowRender={expandedRowRender}
    />
  );
};
